import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import {
  Alert,
  AlertDescription,
} from '../ui/alert';
import { toast } from 'sonner';
import {
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Plus,
  Loader2,
  Eye,
  Ban,
  Settings,
  FileText,
  Users,
  CreditCard,
  Lock,
  Flag,
  Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  site_config: Settings,
  content_block: FileText,
  user_management: Users,
  financial: CreditCard,
  security: Lock,
  feature_flags: Flag,
  data_delete: Trash2
};

const categoryLabels = {
  site_config: 'Site Configuration',
  content_block: 'Content Block',
  user_management: 'User Management',
  financial: 'Financial',
  security: 'Security',
  feature_flags: 'Feature Flags',
  data_delete: 'Data Deletion'
};

const priorityColors = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700'
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-gray-100 text-gray-500',
  cancelled: 'bg-gray-100 text-gray-500'
};

const ApprovalManager = () => {
  const { token, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedTab, setSelectedTab] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [actionNote, setActionNote] = useState('');

  const fetchData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [requestsRes, statsRes, configRes] = await Promise.all([
        fetch(`${API}/admin/approvals/?status=${selectedTab === 'all' ? '' : selectedTab}`, { headers }),
        fetch(`${API}/admin/approvals/stats/summary`, { headers }),
        fetch(`${API}/admin/approvals/config`, { headers })
      ]);
      
      if (requestsRes.ok) {
        const data = await requestsRes.json();
        setRequests(data);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      
      if (configRes.ok) {
        const data = await configRes.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error fetching approval data:', error);
      toast.error('Failed to load approval data');
    } finally {
      setLoading(false);
    }
  }, [token, selectedTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (requestId, action) => {
    setActionLoading(requestId);
    try {
      const response = await fetch(`${API}/admin/approvals/${requestId}/action`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action, note: actionNote })
      });
      
      if (response.ok) {
        toast.success(`Request ${action}d successfully`);
        setSelectedRequest(null);
        setActionNote('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to ${action} request`);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;
    
    setActionLoading(requestId);
    try {
      const response = await fetch(`${API}/admin/approvals/${requestId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Request cancelled');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel request');
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfigSave = async () => {
    try {
      const response = await fetch(`${API}/admin/approvals/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        toast.success('Configuration saved');
        setShowConfigDialog(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h remaining`;
    }
    return `${hours}h ${minutes}m remaining`;
  };

  const canApprove = (request) => {
    // Can't approve own requests
    if (request.requested_by === user?.id) return false;
    // Can't approve non-pending
    if (request.status !== 'pending') return false;
    // Critical requires sysadmin
    if (request.priority === 'critical' && !user?.is_sysadmin) return false;
    return true;
  };

  const canCancel = (request) => {
    if (request.status !== 'pending') return false;
    return request.requested_by === user?.id || user?.is_sysadmin;
  };

  if (loading && !requests.length) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="approval-manager">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.pending_count}</p>
                  <p className="text-sm text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.awaiting_my_review}</p>
                  <p className="text-sm text-muted-foreground">Needs Review</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.approved_today}</p>
                  <p className="text-sm text-muted-foreground">Approved Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.rejected_today}</p>
                  <p className="text-sm text-muted-foreground">Rejected Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Dual Approval System
              </CardTitle>
              <CardDescription>
                Critical changes require approval from a second admin
              </CardDescription>
            </div>
            {user?.is_sysadmin && (
              <Button variant="outline" onClick={() => setShowConfigDialog(true)}>
                <Settings className="w-4 h-4 mr-2" />
                Configure
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="pending">
                Pending
                {stats?.pending_count > 0 && (
                  <Badge className="ml-2 bg-yellow-500">{stats.pending_count}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="approved">Approved</TabsTrigger>
              <TabsTrigger value="rejected">Rejected</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>

            <TabsContent value={selectedTab}>
              {requests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p>No {selectedTab} approval requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {requests.map((request) => {
                    const CategoryIcon = categoryIcons[request.category] || FileText;
                    const isOwnRequest = request.requested_by === user?.id;
                    
                    return (
                      <div
                        key={request.id}
                        className={`border rounded-lg p-4 ${isOwnRequest ? 'bg-blue-50/50 border-blue-200' : ''}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-muted rounded-lg">
                              <CategoryIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium">{request.title}</h4>
                                <Badge className={priorityColors[request.priority]}>
                                  {request.priority}
                                </Badge>
                                <Badge className={statusColors[request.status]}>
                                  {request.status}
                                </Badge>
                                {isOwnRequest && (
                                  <Badge variant="outline" className="border-blue-300 text-blue-600">
                                    Your request
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">
                                {categoryLabels[request.category]} • {request.action} • {request.resource_type}
                              </p>
                              {request.description && (
                                <p className="text-sm mb-2">{request.description}</p>
                              )}
                              <div className="text-xs text-muted-foreground">
                                Requested by <span className="font-medium">{request.requested_by_name}</span> on {formatDate(request.requested_at)}
                                {request.status === 'pending' && (
                                  <span className="ml-2 text-yellow-600">
                                    • {getTimeRemaining(request.expires_at)}
                                  </span>
                                )}
                              </div>
                              {request.status === 'approved' && (
                                <div className="text-xs text-green-600 mt-1">
                                  Approved by {request.approved_by_name} on {formatDate(request.approved_at)}
                                  {request.approval_note && ` • Note: ${request.approval_note}`}
                                </div>
                              )}
                              {request.status === 'rejected' && (
                                <div className="text-xs text-red-600 mt-1">
                                  Rejected by {request.rejected_by_name}: {request.rejection_reason || 'No reason provided'}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedRequest(request)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            {request.status === 'pending' && canApprove(request) && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setActionNote('');
                                  }}
                                  disabled={actionLoading === request.id}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setActionNote('');
                                  }}
                                  disabled={actionLoading === request.id}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                            {canCancel(request) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleCancel(request.id)}
                                disabled={actionLoading === request.id}
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Detail/Action Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Approval Request Details</DialogTitle>
            <DialogDescription>
              Review the proposed changes before taking action
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Category</Label>
                  <p className="font-medium">{categoryLabels[selectedRequest.category]}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Priority</Label>
                  <Badge className={priorityColors[selectedRequest.priority]}>
                    {selectedRequest.priority}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Action</Label>
                  <p className="font-medium capitalize">{selectedRequest.action}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Resource</Label>
                  <p className="font-medium">{selectedRequest.resource_type}</p>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Description</Label>
                <p>{selectedRequest.description || 'No description provided'}</p>
              </div>

              <div>
                <Label className="text-muted-foreground">Proposed Changes</Label>
                <pre className="bg-muted p-3 rounded-md text-sm overflow-auto max-h-48">
                  {JSON.stringify(selectedRequest.changes, null, 2)}
                </pre>
              </div>

              {selectedRequest.status === 'pending' && canApprove(selectedRequest) && (
                <div>
                  <Label>Add a note (optional)</Label>
                  <Textarea
                    value={actionNote}
                    onChange={(e) => setActionNote(e.target.value)}
                    placeholder="Reason for approval or rejection..."
                    rows={2}
                  />
                </div>
              )}

              {selectedRequest.status === 'pending' && selectedRequest.requested_by === user?.id && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    You cannot approve your own requests. Another admin must review and approve this change.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedRequest(null)}>
              Close
            </Button>
            {selectedRequest?.status === 'pending' && canApprove(selectedRequest) && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => handleAction(selectedRequest.id, 'reject')}
                  disabled={actionLoading === selectedRequest?.id}
                >
                  {actionLoading === selectedRequest?.id ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4 mr-2" />
                  )}
                  Reject
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handleAction(selectedRequest.id, 'approve')}
                  disabled={actionLoading === selectedRequest?.id}
                >
                  {actionLoading === selectedRequest?.id ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Approve & Apply
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Configuration Dialog (Sysadmin Only) */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dual Approval Configuration</DialogTitle>
            <DialogDescription>
              Configure which changes require dual approval
            </DialogDescription>
          </DialogHeader>

          {config && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Dual Approval</Label>
                  <p className="text-sm text-muted-foreground">
                    Require second admin approval for critical changes
                  </p>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Critical Requires Sysadmin</Label>
                  <p className="text-sm text-muted-foreground">
                    Only sysadmins can approve critical priority changes
                  </p>
                </div>
                <Switch
                  checked={config.critical_requires_sysadmin}
                  onCheckedChange={(v) => setConfig({ ...config, critical_requires_sysadmin: v })}
                />
              </div>

              <div>
                <Label>Default Expiration (hours)</Label>
                <Input
                  type="number"
                  value={config.default_expiration_hours}
                  onChange={(e) => setConfig({ 
                    ...config, 
                    default_expiration_hours: parseInt(e.target.value) || 48 
                  })}
                  min={1}
                  max={168}
                />
              </div>

              <div>
                <Label>Categories Requiring Approval</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {Object.entries(categoryLabels).map(([key, label]) => (
                    <div key={key} className="flex items-center gap-2">
                      <Switch
                        checked={config.categories_requiring_approval?.includes(key)}
                        onCheckedChange={(checked) => {
                          const cats = config.categories_requiring_approval || [];
                          setConfig({
                            ...config,
                            categories_requiring_approval: checked
                              ? [...cats, key]
                              : cats.filter(c => c !== key)
                          });
                        }}
                      />
                      <span className="text-sm">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfigSave}>
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalManager;
